import React, { useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";
import { useQuery } from "@tanstack/react-query";
import { API_URL } from "../../../services/apiConfig";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import axiosInstance from "../../../services/axios.instance";
import { useForm } from "react-hook-form";
import { ConfirmationModal } from "../../../components";
import { SUCCESS_MESSAGES, ERROR_MESSAGES } from "../../../utils/messages";
interface ServiceType {
  id: number;
  serviceType: string;
  serviceName: string;
  status: boolean;
  type: string;
}

interface OrderDetails {
  order_service_details?: {
    referenceNumber?: string;
    serviceName?: string;
  };
  aaa_service_request_model?: {
    state?: string;
  };
  recollection_location?: {
    address?: string;
  };
  delivery_location?: {
    address?: string;
  };
}

interface FormData {
  locationTo: string;
  serviceType: string;
}

const ChangeService: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const OrderId = new URLSearchParams(location.search).get("OrderId");
  const [searchParams] = useSearchParams();
  const page = searchParams.get("page") || "1";
  const size = searchParams.get("size") || "10";

  const handleBack = () => {
    navigate({
      pathname: "/dashboard/servicelist",
      search: `?page=${page}&size=${size}`,
    });
  };

  const {
    data: orderDetails,
    isLoading,
    error,
  } = useQuery<OrderDetails>({
    queryKey: ["orderDetails", OrderId],
    queryFn: async () => {
      if (!OrderId) throw new Error("Order ID is required");
      const response = await axiosInstance.get(
        `${API_URL.SERVICE_DETAILS}/${OrderId}`,
        {
          headers: {
            location: "0,0",
            language: "english",
          },
        }
      );
      return response.data;
    },
  });

  const { data: serviceTypes, isLoading: isLoadingServiceTypes } = useQuery<
    ServiceType[]
  >({
    queryKey: ["serviceType"],
    queryFn: async () => {
      const response = await axiosInstance.get(API_URL.LOCATION_BASED_SERVICE);
      return response.data;
    },
  });

  const { watch, setValue } = useForm<FormData>({
    defaultValues: {
      locationTo: "",
      serviceType: "",
    },
  });

  const [addressSuggestions, setAddressSuggestions] = useState<string[]>([]);
  const [showSuggestions, setShowSuggestions] = useState<boolean>(false);
  const [showConfirmationModal, setShowConfirmationModal] =
    useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [selectedAddress, setSelectedAddress] = useState<{
    address: string;
    latitude: string;
    longitude: string;
  } | null>(null);
  const searchQuery = watch("locationTo");

  const fetchLocationSuggestions = useCallback(async (query: string) => {
    if (!query.trim()) {
      setAddressSuggestions([]);
      return;
    }

    try {
      const response = await axiosInstance.get<
        Array<{ formatted_address: string }>
      >(`${API_URL.GET_GOOGLELOCATIONS}?keyword=${encodeURIComponent(query)}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
        },
      });
      const suggestions = response.data.map((item) => item.formatted_address);
      setAddressSuggestions(suggestions);
    } catch (error) {
      console.error("Error fetching location suggestions:", error);
      setAddressSuggestions([]);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery) {
        fetchLocationSuggestions(searchQuery);
      } else {
        setAddressSuggestions([]);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, fetchLocationSuggestions]);

  const handleLocationInputChange = useCallback(
    (value: string) => {
      setValue("locationTo", value);
      setShowSuggestions(true);
    },
    [setValue]
  );

  const handleSuggestionClick = useCallback(
    (suggestion: string, lat: string, lng: string) => {
      setValue("locationTo", suggestion);
      setSelectedAddress({
        address: suggestion,
        latitude: lat,
        longitude: lng,
      });
      setShowSuggestions(false);
    },
    [setValue]
  );

  const handleSubmit = () => {
    if (!watch("serviceType") || !watch("locationTo") || !selectedAddress) {
      toast.error(ERROR_MESSAGES.SELECT_SERVICE_LOCATION);
      return;
    }
    setShowConfirmationModal(true);
  };

  const confirmUpdate = async () => {
    if (!OrderId) {
      toast.error(ERROR_MESSAGES.ORDER_ID_MISSING);
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        order_number: OrderId,
        service_name: watch("serviceType"),
        service_type: "LOCATION_BASED",
        drop_off_location: {
          addressId: null,
          addressType: null,
          address: selectedAddress?.address,
          street: "",
          city: "",
          district: "",
          state: "",
          country: "",
          pinCode: "",
          mobileNumber: null,
          latitude: selectedAddress?.latitude,
          longitude: selectedAddress?.longitude,
          landMark: "",
        },
      };

      const response = await axiosInstance.post(
        `${API_URL.SERVICE_DETAILS_EDIT}`,
        payload
      );

      if (response.data) {
        toast.success(SUCCESS_MESSAGES.SERVICE_UPDATED);
        navigate("/dashboard/servicelist");
      }
    } catch (error) {
      console.error("Error updating service:", error);
      toast.error(ERROR_MESSAGES.SERVICE_UPDATE_FAILED);
    } finally {
      setIsSubmitting(false);
      setShowConfirmationModal(false);
    }
  };

  if (isLoading || isLoadingServiceTypes) {
    return (
      <div className="container mt-4 d-flex justify-content-center">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (error instanceof Error) {
    return (
      <div className="container mt-4">
        <div className="alert alert-danger">
          Error loading service details: {error.message}
        </div>
      </div>
    );
  }

  return (
    <div className="col-12">
      <div className="row">
        <div className="col-md-12 text-start div-bg-invoice-details pt-3">
          <div className="card card-bg-invoice-details">
            <div className="card-body px-5">
              <div>
                <button onClick={handleBack} className="all_button_style">
                  Back
                </button>
              </div>
              <div className="row pb-3">
                <div className="table-responsive pt-3">
                  <div className="mb-3 text-center invoice-text-yellow-afl">
                    <h4>
                      Job Reference No:{" "}
                      <span className="">
                        {orderDetails?.order_service_details?.referenceNumber}
                      </span>
                    </h4>
                  </div>
                  <div className="row pt-3">
                    <div className="col-md-6 pt-3">
                      <div className="invoice-div-head">Service Type</div>
                      <div className="invoice-div-data">
                        {orderDetails?.order_service_details?.serviceName}
                      </div>
                    </div>
                    <div className="col-md-6 pt-3">
                      <div className="invoice-div-head">Service Status</div>
                      <div className="invoice-div-data">
                        {orderDetails?.aaa_service_request_model?.state}
                      </div>
                    </div>
                    {orderDetails?.recollection_location ? (
                      <>
                        <div className="col-md-6 pt-3">
                          <div className="invoice-div-head">Location From</div>
                          <div className="invoice-div-data">
                            {orderDetails?.delivery_location?.address}
                          </div>
                        </div>
                        <div className="col-md-6 pt-3">
                          <div className="invoice-div-head">Location To</div>
                          <div className="invoice-div-data">
                            {orderDetails?.recollection_location?.address}
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="col-md-6 pt-3">
                        <div className="invoice-div-head">Location To</div>
                        <div className="invoice-div-data">
                          {orderDetails?.delivery_location?.address}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Input Fields */}
              <div className="row pt-2">
                <div className="col-md-6 pt-3">
                  <label className="form-label dashboard-poppins-font-style">
                    Service Type<span className="merchantStar">*</span>
                  </label>
                  <select
                    className="select-invoice-bg-black-edit dashboard-poppins-font-style"
                    onChange={(e) => setValue("serviceType", e.target.value)}
                    value={watch("serviceType")}
                  >
                    <option value="">Select Service Type</option>
                    {serviceTypes?.map((service) => (
                      <option key={service.id} value={service.serviceName}>
                        {service.serviceName}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-6 pt-3 position-relative">
                  <label className="form-label dashboard-poppins-font-style">
                    Location To<span className="merchantStar">*</span>
                  </label>
                  <div className="position-relative">
                    <input
                      type="text"
                      className="form-control merchant-input-style-create-product"
                      placeholder="Search location..."
                      value={searchQuery ?? ""}
                      onChange={(e) =>
                        handleLocationInputChange(e.target.value)
                      }
                    />
                    {showSuggestions && addressSuggestions.length > 0 && (
                      <div
                        className="position-absolute w-100 bg-white border rounded mt-1"
                        style={{
                          zIndex: 1000,
                          maxHeight: "200px",
                          overflowY: "auto",
                        }}
                      >
                        <ul className="list-unstyled mb-0">
                          {addressSuggestions.map((suggestion, index) => {
                            const lat = "25.2048"; // Replace if actual lat/lng data is returned
                            const lng = "55.2708";
                            return (
                              <li
                                key={index}
                                className="p-2 hover-bg-light"
                                style={{ cursor: "pointer" }}
                                onClick={() =>
                                  handleSuggestionClick(suggestion, lat, lng)
                                }
                              >
                                {suggestion}
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Submit Button */}
              <div className="row mt-4 mb-4">
                <div className="col-12 text-end">
                  <button
                    className="invoice-approve-btn px-4"
                    onClick={handleSubmit}
                    disabled={
                      isSubmitting ||
                      !watch("serviceType") ||
                      !watch("locationTo")
                    }
                  >
                    {isSubmitting && (
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                      />
                    )}
                    Update Service
                  </button>
                </div>
              </div>

              {/* Confirmation Modal */}
              <ConfirmationModal
                isOpen={showConfirmationModal}
                onClose={() => setShowConfirmationModal(false)}
                onConfirm={confirmUpdate}
                message={ERROR_MESSAGES.CONFIRM_SERVICE_UPDATE}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChangeService;
